(() => {
  'use strict';
  function sameSizeColours(variants, selected) {
    if (!selected || selected.available || !selected.option2) return [];
    return variants.filter(candidate => candidate.available &&
      candidate.option1 !== selected.option1 &&
      candidate.option2 === selected.option2 &&
      (candidate.option3 || '') === (selected.option3 || ''));
  }
  function init(root) {
    if (root.dataset.initialized || !root.querySelector('[data-single-data]')) return;
    root.dataset.initialized = 'true';
    const data = JSON.parse(root.querySelector('[data-single-data]').textContent);
    const form = root.querySelector('form'), select = root.querySelector('[data-single-variant]');
    const button = root.querySelector('[data-single-submit]'), status = root.querySelector('[data-single-status]');
    const error = root.querySelector('[data-single-error]'), cart = root.querySelector('[data-single-cart]');
    const money = cents => new Intl.NumberFormat(data.locale === 'ar' ? 'ar-EG' : 'en-EG', {style:'currency',currency:'EGP'}).format(cents / 100);
    const buy = root.querySelector('[data-single-buy-now]');
    let checkoutURL = null;
    try {
      const target = new URL(data.checkoutUrl, location.origin);
      if (target.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout$/i.test(target.pathname)) checkoutURL = target.href;
    } catch (_) {}
    let busy = false, added = false, uncertain = false, leaving = false, buying = false, checkoutIntent = 0;
    window.addEventListener('pagehide', () => { checkoutIntent++; });
    window.addEventListener('popstate', () => { checkoutIntent++; });
    const checkoutCart = async () => {
      const response = await fetch(data.cartUrl, {headers:{Accept:'application/json'},cache:'no-store'});
      if (!response.ok) throw new Error();
      const result = await response.json();
      if (!Array.isArray(result.items)) throw new Error();
      return result;
    };
    const itemQuantity = (cart, id) => cart.items.filter(item => String(item.variant_id || item.id) === String(id)).reduce((sum,item) => sum + item.quantity, 0);
    const verifiedCart = (before, after, submitted) => submitted.every(item => itemQuantity(after, item.id) >= itemQuantity(before, item.id) + item.quantity) && before.items.every(item => itemQuantity(after, item.variant_id || item.id) >= itemQuantity(before, item.variant_id || item.id));
    const current = () => data.variants.find(v => String(v.id) === select.value);
    const options = root.querySelector('[data-pdp-options]');
    const optionsSupported = Boolean(options && window.CSS?.supports?.('selector(:has(*))'));
    const optionLabels = options ? JSON.parse(options.querySelector('[data-pdp-labels]').textContent) : null;
    const extras = options ? [...options.querySelectorAll('[data-pdp-extra]')].map(row => ({row, select: row.querySelector('[data-pdp-variant]'), variants: JSON.parse(row.querySelector('[data-pdp-extra-data]').textContent) || []})) : [];
    function makeGrid(container, variantSelect, variants) {
      const element = container.querySelector('[data-variant-grid]');
      if (!element) return null;
      const groups = [...element.querySelectorAll('[data-grid-option]')];
      let values = groups.map(() => ''), lastValue;
      const hint = element.querySelector('[data-grid-hint]');
      const optionValue = (variant, index) => String(variant['option' + (index + 1)] || '');
      const sync = force => {
        if (force || variantSelect.value !== lastValue) {
          const variant = variants.find(item => String(item.id) === variantSelect.value);
          values = groups.map((_, index) => variant ? optionValue(variant, index) : '');
          lastValue = variantSelect.value;
        }
      };
      const paint = () => {
        sync(false);
        groups.forEach((group, index) => {
          group.querySelectorAll('[data-grid-value]').forEach(input => {
            const available = variants.some(variant => variant.available && optionValue(variant, index) === input.value && values.every((value, other) => other >= index || !value || optionValue(variant, other) === value));
            input.checked = values[index] === input.value;
            input.disabled = busy || !available;
            const label = input.closest('label');
            label.dataset.soldOut = String(!available);
            label.querySelector('[data-grid-sold-out]').hidden = available;
          });
        });
        hint.hidden = values.every(Boolean);
      };
      element.addEventListener('change', event => {
        const input = event.target.closest('[data-grid-value]');
        if (!input || busy || input.disabled) return;
        const index = groups.indexOf(input.closest('[data-grid-option]'));
        values[index] = input.value;
        for (let next = index + 1; next < values.length; next++) {
          if (values[next] && !variants.some(variant => variant.available && values.every((value, other) => other > next || !value || optionValue(variant, other) === value))) values[next] = '';
        }
        const match = values.every(Boolean) ? variants.find(variant => values.every((value, other) => optionValue(variant, other) === value)) : null;
        variantSelect.value = match ? String(match.id) : '';
        lastValue = variantSelect.value;
        paint();
        variantSelect.dispatchEvent(new Event('change', {bubbles:true}));
      });
      variantSelect.hidden = true;
      variantSelect.required = false;
      container.querySelector('[data-variant-fallback-label]')?.setAttribute('hidden', '');
      element.hidden = false;
      sync(true);
      return {paint, sync, colour:() => values[0], focus:() => element.querySelector('input:checked:not(:disabled),input:not(:disabled)')?.focus()};
    }
    const primaryGrid = makeGrid(root, select, data.variants);
    extras.forEach(extra => { extra.grid = makeGrid(extra.row, extra.select, extra.variants); });
    let cartSnapshot = null, cartRead = 0;
    const complete = () => optionsSupported && Boolean(options?.querySelector('input[value="complete"]:checked'));
    const chosen = () => [current(), ...(complete() ? extras.map(extra => extra.variants.find(v => String(v.id) === extra.select.value)) : [])];
    const selectionReady = () => chosen().every(v => v?.available);
    const selectionKey = 'sitrPdpOptionsV1';
    const saveSelection = () => {
      if (!optionsSupported || busy) return;
      const state = history.state && typeof history.state === 'object' ? history.state : {};
      const saved = state[selectionKey] && typeof state[selectionKey] === 'object' ? state[selectionKey] : {};
      const selection = {mode:complete() ? 'complete' : 'single', variant:select.value, extras:extras.map(extra => extra.select.value)};
      try { history.replaceState({...state,[selectionKey]:{...saved,[root.id]:selection}}, '', location.href); } catch (_) {}
    };
    const restoreSelection = () => {
      const saved = history.state?.[selectionKey]?.[root.id];
      if (!optionsSupported || !saved || !['single','complete'].includes(saved.mode)) return false;
      const primary = data.variants.find(v => String(v.id) === saved.variant);
      select.value = primary ? String(primary.id) : '';
      options.querySelectorAll('.sitr-pdp-options__choices input[type="radio"]').forEach(input => { input.checked = input.value === saved.mode; });
      extras.forEach((extra,index) => {
        const variant = extra.variants.find(v => String(v.id) === saved.extras?.[index]);
        extra.select.value = variant ? String(variant.id) : '';
      });
      return true;
    };

    const readCart = async () => {
      if (!optionLabels) return;
      const request = ++cartRead;
      try {
        const response = await fetch(optionLabels.cartUrl, {headers:{Accept:'application/json'}, cache:'no-store'});
        if (!response.ok) throw new Error();
        const result = await response.json();
        if (request === cartRead) cartSnapshot = result;
      } catch (_) { if (request === cartRead) cartSnapshot = null; }
    };
    const refreshOptions = () => {
      if (!optionsSupported) return;
      const enabled = complete();
      options.querySelector('[data-pdp-extras]').removeAttribute('hidden');
      options.querySelectorAll('.sitr-pdp-options__choices input[type="radio"]').forEach(input => input.disabled = busy);
      extras.forEach(extra => {
        extra.select.disabled = busy; extra.select.required = false; extra.select.setAttribute('aria-required', String(enabled));
        const selectedExtra = extra.variants.find(v => String(v.id) === extra.select.value);
        const image = extra.row.querySelector('img');
        if (image && !selectedExtra) image.hidden = true;
        if (image && selectedExtra) {
          image.hidden = !selectedExtra.featured_image?.src;
          if (selectedExtra.featured_image?.src) { image.src = selectedExtra.featured_image.src; image.alt = selectedExtra.title || ''; image.removeAttribute('srcset'); }
        }
      });
      const summary = options.querySelector('[data-pdp-summary]');
      summary.replaceChildren();
      if (!enabled) return;
      if (!selectionReady()) return;
      const items = chosen(), subtotal = items.reduce((sum,v) => sum + v.price,0);
      const canEstimate = cartSnapshot && cartSnapshot.item_count === 0 && !cartSnapshot.cart_level_discount_applications?.length && !cartSnapshot.discount_codes?.length;
      if (canEstimate) {
        const rate = items.length >= 3 ? 0.1 : 0.05;
        const saving = items.reduce((sum,v) => sum + Math.round(v.price * rate),0);
        const amount = document.createElement('bdi'); amount.textContent = money(subtotal - saving);
        const totalLine = document.createElement('span'); totalLine.append(optionLabels.estimate + ': ', amount);
        const badge = document.createElement('span'); badge.className = 'sitr-pdp-options__save';
        const saved = document.createElement('bdi'); saved.textContent = money(saving);
        badge.append(optionLabels.save + ' ',saved); summary.append(badge, document.createElement('br'), totalLine);
        const extraLine = document.createElement('span'); extraLine.className = 'sitr-pdp-options__note';
        const extraAmount = document.createElement('bdi'); extraAmount.textContent = money(subtotal - saving - current().price);
        extraLine.append(optionLabels.extra + ': ', extraAmount); summary.append(document.createElement('br'), extraLine);
      } else {
        summary.textContent = optionLabels.pending;
      }
    };
    if (optionsSupported) {
      options.hidden = false;
      options.addEventListener('pointerdown', () => { if (!busy) refresh(); }, true);
      options.addEventListener('focusin', () => { if (!busy) refresh(); }, true);
      options.addEventListener('input', event => { if (!busy && !event.target.closest('[data-variant-grid]')) refresh(); });
      options.addEventListener('change', async event => {
        if (event.target.closest('[data-variant-grid]')) return;
        if (busy) return;
        if (uncertain) { refresh(); return; }
        added = false; status.textContent = ''; error.hidden = true; cart.hidden = true;
        saveSelection();
        cartRead++; cartSnapshot = null;
        refresh();
        if (complete()) { await readCart(); refresh(); }
      });
    }

    const languageForm = document.querySelector('#SitrLanguage');
    const syncReturnTo = () => {
      const inputs = languageForm?.querySelectorAll('[name="return_to"]');
      if (!inputs?.length) return;
      const destination = new URL(location.href);
      // Shopify's localization redirect removes return_to fragments. Carry only
      // these storefront anchors through that redirect, then remove the marker.
      const anchor = location.hash.slice(1);
      if (/^(single-size-guide|sitr-browse-offer|purchase-[A-Za-z0-9_-]+)$/.test(anchor)) destination.searchParams.set('sitr_fragment', anchor);
      else destination.searchParams.delete('sitr_fragment');
      inputs.forEach(input => { input.value = destination.pathname + destination.search + destination.hash; });
    };
    const syncVariantURL = () => {
      const url = new URL(location.href);
      const variant = current();
      if (variant) url.searchParams.set('variant', String(variant.id));
      else url.searchParams.delete('variant');
      if (url.href !== location.href) history.replaceState(history.state, '', url.href);
      syncReturnTo();
    };
    languageForm?.addEventListener('click', syncReturnTo, true);
    languageForm?.addEventListener('submit', syncReturnTo, true);
    const gallery = root.closest('.sitr-experience__intro')?.previousElementSibling;
    const galleryViewport = gallery?.querySelector('[data-single-gallery-viewport]');
    if (galleryViewport) {
      const slides = [...galleryViewport.querySelectorAll('[data-single-gallery-slide]')];
      const controls = [...gallery.querySelectorAll('[data-single-gallery-select]')];
      const previous = gallery.querySelector('[data-gallery-prev]'), next = gallery.querySelector('[data-gallery-next]');
      let active = 0, scrollFrame;
      const markActive = index => {
        active = Math.max(0, Math.min(slides.length - 1, index));
        controls.forEach(control => control.setAttribute('aria-pressed', String(control.dataset.singleGallerySelect === slides[active].dataset.singleGallerySlide)));
        if (slides.some((slide, index) => index !== active && slide.contains(document.activeElement))) galleryViewport.focus({preventScroll:true});
        slides.forEach((slide, index) => {
          slide.setAttribute('aria-hidden', String(index !== active));
          slide.querySelectorAll('button').forEach(control => { control.tabIndex = index === active ? 0 : -1; });
        });
        previous.disabled = active === 0; next.disabled = active === slides.length - 1;
      };
      const move = index => {
        index = Math.max(0, Math.min(slides.length - 1, index));
        const target = slides[index].getBoundingClientRect(), frame = galleryViewport.getBoundingClientRect();
        galleryViewport.scrollBy({left:target.left + target.width / 2 - frame.left - frame.width / 2, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        markActive(index);
      };
      slides.forEach(slide => { slide.hidden = false; });
      galleryViewport.dataset.sliderReady = 'true';
      controls.forEach(control => control.addEventListener('click', () => move(slides.findIndex(slide => slide.dataset.singleGallerySlide === control.dataset.singleGallerySelect))));
      previous.addEventListener('click', () => move(active - 1));
      next.addEventListener('click', () => move(active + 1));
      galleryViewport.addEventListener('keydown', event => {
        if (event.ctrlKey || event.metaKey || event.altKey || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(galleryViewport).direction === 'rtl';
        move(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : active + ((event.key === 'ArrowRight') !== rtl ? 1 : -1));
      });
      galleryViewport.addEventListener('scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const frame = galleryViewport.getBoundingClientRect(), center = frame.left + frame.width / 2;
          let closest = 0, distance = Infinity;
          slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect(), current = Math.abs(rect.left + rect.width / 2 - center);
            if (current < distance) { closest = index; distance = current; }
          });
          markActive(closest);
        });
      }, {passive:true});
      markActive(0);
    }
    function refresh() {
      const variant = current();
      button.disabled = busy || leaving || uncertain || !selectionReady();
      if (buy) {
        buy.disabled = button.disabled || !checkoutURL;
        buy.textContent = buying && (busy || leaving) ? data.labels.opening : data.labels.buyNow;
      }
      button.textContent = busy ? data.labels.adding : variant && !variant.available ? (data.locale === 'ar' ? 'غير متاح' : 'Sold out') : added ? (complete() ? optionLabels.added : data.labels.added) : complete() ? optionLabels.add : data.labels.add;
      refreshOptions();
      primaryGrid?.paint();
      extras.forEach(extra => extra.grid?.paint());
      select.disabled = busy;
      const alternatives = root.querySelector('[data-single-alternatives]');
      alternatives.replaceChildren();
      alternatives.hidden = !variant || variant.available;
      if (variant && !variant.available) {
        const note = document.createElement('p');
        note.textContent = data.locale === 'ar' ? 'الاختيار غير متاح. اختاري لوناً آخر بنفس المقاس:' : variant.title + ' is sold out. Choose another colour in the same size:';
        alternatives.append(note);
        sameSizeColours(data.variants, variant).forEach(candidate => {
          const control = document.createElement('button');
          control.type = 'button'; control.disabled = busy;
          const colour = data.locale === 'ar' ? (candidate.option1 === 'Olive' ? 'زيتوني' : candidate.option1 === 'Dark Navy' ? 'كحلي داكن' : candidate.option1) : candidate.option1;
          control.textContent = (data.locale === 'ar' ? 'اختاري ' : 'Choose ') + colour + ' / ' + candidate.option2;
          control.addEventListener('click', () => {
            if (busy) return;
            select.value = String(candidate.id);
            select.dispatchEvent(new Event('change', {bubbles:true}));
            if (primaryGrid) primaryGrid.focus(); else select.focus();
          });
          alternatives.append(control);
        });
        if (alternatives.children.length === 1) note.textContent = data.locale === 'ar' ? 'هذا المقاس غير متاح في الألوان الأخرى حالياً.' : variant.title + ' is sold out. No other colour is currently available in this size.';
      }
      if (variant) root.querySelector('[data-single-price]').textContent = money(variant.price);
      // A variant image represents the selected colour. Never substitute another colour.
      const galleryVariant = variant || data.variants.find(v=>v.available) || data.variants[0];
      const chosenColour = primaryGrid?.colour() || galleryVariant?.option1;
      const galleryColour = ({'زيتي':'Olive','زيتوني':'Olive','كحلي داكن':'Dark Navy'})[chosenColour] || chosenColour;
      const image = gallery?.querySelector('[data-single-styled] img');
      const source = data.modelImages?.[galleryColour] || data.images?.[galleryColour] || galleryVariant?.featured_image?.src;
      if (image && source) {
        image.src = source; image.removeAttribute('srcset'); image.alt = data.title + ' · ' + (variant?.title || galleryColour);
        const note = gallery.querySelector('[data-single-image-note]');
        if (note) note.textContent = data.modelImages ? data.singleStyled : galleryColour === 'Olive' && ['performance-top','hip-cover'].includes(data.handle) ? data.styledSet : galleryColour;
        const trigger = image.closest('[data-sitr-image]');
        if (trigger) trigger.dataset.fullImage = source;
      }
      const clean=gallery?.querySelector('[data-single-clean]'),cleanSource=data.images?.[galleryColour] || galleryVariant?.featured_image?.src;
      if(clean&&cleanSource){const img=clean.querySelector('img');img.src=cleanSource;img.removeAttribute('srcset');img.alt=data.title+' · '+(variant?.title || galleryColour);clean.dataset.fullImage=cleanSource;const note=gallery.querySelector('[data-single-clean-note]');if(note)note.textContent=galleryColour==='Olive'&&['performance-top','hip-cover'].includes(data.handle)?data.styledSet:data.title+' · '+galleryColour;}
      gallery?.querySelectorAll('[data-single-detail-colour]').forEach(view=>view.hidden=view.dataset.singleDetailColour!==galleryColour);
    }
    select.addEventListener('change', () => { added = false; if (!uncertain) { status.textContent = ''; error.hidden = true; cart.hidden = true; } syncVariantURL(); saveSelection(); refresh(); });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!busy) refresh();
      const variant = current(); if (busy || leaving || uncertain || !selectionReady()) return;
      const checkout = event.submitter === buy;
      if (checkout && !checkoutURL) return;
      const intent = ++checkoutIntent;
      const submittedComplete = complete();
      const submittedItems = chosen().map(v => ({id:v.id,quantity:1}));
      busy = true; buying = checkout; added = false; status.textContent = ''; error.hidden = true; cart.hidden = true; refresh();
      let updateAttempted = false;
      try {
        const before = checkout ? await checkoutCart() : null;
        if (checkout && intent !== checkoutIntent) return;
        updateAttempted = true;
        const response = await fetch(data.addUrl, {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:submittedItems})});
        const result = await response.json(); if (!response.ok) throw new Error(result.description || data.labels.failed);
        added = true; status.textContent = submittedComplete ? optionLabels.added : data.labels.added; cart.hidden = false;
        if (submittedComplete) {
          await readCart();
          if (cartSnapshot) {
            status.append(' · ' + optionLabels.actual + ': ' + money(cartSnapshot.total_price));
            if (cartSnapshot.total_discount > 0) status.append(' · ' + optionLabels.actualSave + ': ' + money(cartSnapshot.total_discount));
          }
        }
        if (checkout && intent === checkoutIntent) {
          const after = await checkoutCart();
          if (!verifiedCart(before, after, submittedItems)) throw new Error();
          leaving = intent === checkoutIntent;
          status.textContent = data.labels.opening;
        }
        document.dispatchEvent(new CustomEvent('sitr:cart-added', {detail:{items:result.items || [],inlineConfirmation:true}}));
        if (checkout && leaving) location.assign(checkoutURL);
      } catch (_) {
        leaving = false;
        if (updateAttempted) {
          uncertain = true;
          await readCart();
          error.textContent = data.labels.uncertain;
        } else error.textContent = data.labels.failed;
        error.hidden = false; cart.hidden = false;
      }
      finally { busy = false; if (!leaving) buying = false; refresh(); }
    });
    const restore = event => {
      if (busy) return;
      if (uncertain && event?.type === 'popstate') { refresh(); return; }
      const url = new URL(location.href);
      const anchor = url.searchParams.get('sitr_fragment');
      if (anchor !== null) {
        url.searchParams.delete('sitr_fragment');
        const validAnchor = /^(single-size-guide|sitr-browse-offer|purchase-[A-Za-z0-9_-]+)$/.test(anchor);
        if (validAnchor) url.hash = anchor;
        history.replaceState(history.state, '', url.href);
        if (validAnchor) document.getElementById?.(anchor)?.scrollIntoView();
      }
      const linked = data.variants.find(v => String(v.id) === new URLSearchParams(location.search).get('variant'));
      // Keep known sold-out variants visible, but never reuse a stale or foreign ID.
      if (!restoreSelection()) select.value = linked ? String(linked.id) : '';
      primaryGrid?.sync(true);
      extras.forEach(extra => extra.grid?.sync(true));
      syncVariantURL();
      if (options) { cartRead++; cartSnapshot = null; uncertain = false; }
      busy = false; leaving = false; buying = false; added = false; status.textContent = ''; cart.hidden = true; error.hidden = true; refresh();
      if (complete()) readCart().then(refresh);
    };
    const scheduleRestore = event => {
      setTimeout(() => {
        restore(event);
        requestAnimationFrame(() => { if (!busy) refresh(); });
      }, 0);
    };
    document.addEventListener('visibilitychange', () => { if (!document.hidden && !busy) refresh(); });
    window.addEventListener('pagehide', saveSelection);
    window.addEventListener('pagereveal', scheduleRestore);
    window.addEventListener('pageshow', scheduleRestore); window.addEventListener('popstate', scheduleRestore); restore();
  }
  document.querySelectorAll('[data-sitr-single]').forEach(init);
  document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('[data-sitr-single]').forEach(init));
})();



